import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { SIDEBAR_GROUPS, SUPER_ADMIN_GROUP } from "@/components/layout/navConfig";
import { Search, ArrowRight, ArrowLeft } from "lucide-react";
import { Input } from "@/components/ui/input";

/**
 * AllTools — categorised directory of every route in the app.
 *
 * Powered by the same `SIDEBAR_GROUPS` config that drives the sidebar,
 * so we never drift out of sync. Search filters across group + child
 * labels. Role gates are respected — a viewer will never see admin-only
 * tools here.
 */
export default function AllTools() {
    const { user } = useAuth();
    const [q, setQ] = useState("");
    const canSee = (roles) => !roles || roles.includes(user?.role);

    const groups = useMemo(() => {
        const base = SIDEBAR_GROUPS
            .filter((g) => canSee(g.roles))
            .map((g) => {
                const children = g.children
                    ? g.children.filter((c) => canSee(c.roles))
                    : [{ to: g.to, label: g.label, icon: g.icon }];
                if (q) {
                    const needle = q.toLowerCase();
                    const filtered = children.filter((c) => c.label.toLowerCase().includes(needle));
                    return { ...g, children: filtered };
                }
                return { ...g, children };
            })
            .filter((g) => g.children.length > 0);

        // Append Super Admin as its own category — only for platform admins.
        // P0 Multi-Tenant Security — Platform-Admin split. A regular tenant
        // admin must never see the SaaS-ops toolkit even here.
        if (user?.platform_admin === true && canSee(SUPER_ADMIN_GROUP.roles)) {
            const superChildren = SUPER_ADMIN_GROUP.children.filter((c) => canSee(c.roles));
            const filteredSuper = q
                ? superChildren.filter((c) => c.label.toLowerCase().includes(q.toLowerCase()))
                : superChildren;
            if (filteredSuper.length > 0) {
                base.push({ ...SUPER_ADMIN_GROUP, children: filteredSuper });
            }
        }
        return base;
    }, [q, user?.role, user?.platform_admin]);

    return (
        <div className="max-w-[1400px] mx-auto" data-testid="all-tools-page">
            <Link to="/" className="inline-flex items-center gap-1 text-xs text-stone-500 hover:text-stone-900 mb-3">
                <ArrowLeft size={12} /> Back to Concierge
            </Link>
            <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
                <div>
                    <div className="eyebrow">All tools</div>
                    <h1 className="font-display text-4xl sm:text-5xl text-stone-900 leading-none mt-2">Every tool in one place.</h1>
                    <p className="text-sm text-stone-500 mt-3 max-w-xl">The full VAULT toolkit — grouped by workflow. Everything you don&apos;t see on the Concierge lives here.</p>
                </div>
                <div className="relative w-full sm:w-80">
                    <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
                    <Input
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="Search 100+ tools…"
                        className="pl-9"
                        data-testid="all-tools-search"
                    />
                </div>
            </div>

            {groups.length === 0 ? (
                <div className="py-16 text-center text-sm text-stone-500" data-testid="all-tools-empty">
                    No tools match &ldquo;{q}&rdquo;.
                </div>
            ) : groups.map((g) => (
                <section key={g.id} className="mb-8" data-testid={`tools-group-${g.id}`}>
                    <div className="flex items-center gap-2 mb-3">
                        <g.icon size={14} className="text-stone-500" />
                        <h2 className="text-[11px] font-bold uppercase tracking-[0.2em] text-stone-500">{g.label}</h2>
                        <span className="text-[10px] text-stone-400">· {g.children.length} tool{g.children.length === 1 ? "" : "s"}</span>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2">
                        {g.children.map((c) => (
                            <Link
                                key={`${g.id}-${c.to}`}
                                to={c.to}
                                data-testid={`tool-${g.id}-${c.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                                className="group flex items-center gap-2.5 p-3 rounded-xl border border-stone-200 bg-white hover:border-amber-400 hover:shadow-sm transition-all"
                            >
                                <span className="w-9 h-9 rounded-lg bg-stone-100 group-hover:bg-amber-50 flex items-center justify-center shrink-0">
                                    <c.icon size={15} strokeWidth={1.7} className="text-stone-700" />
                                </span>
                                <div className="flex-1 min-w-0">
                                    <div className="text-[13.5px] font-medium text-stone-900 truncate">{c.label}</div>
                                    <div className="text-[10px] text-stone-500 mt-0.5">
                                        <span className="inline-block px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 uppercase tracking-widest font-semibold">Active</span>
                                    </div>
                                </div>
                                <ArrowRight size={12} className="text-stone-300 group-hover:text-amber-600 shrink-0" />
                            </Link>
                        ))}
                    </div>
                </section>
            ))}
        </div>
    );
}
